import React, { useState, useMemo } from 'react';
import { Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faPlus, faShieldAlt,
  faUserTag, faTags, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { useUserPermissions, useCapabilities, useRoles, useResourceTypes } from '../hooks/useAuthorizationData';
import RoleDetailPage from './RoleDetailPage';
import CapabilityAssignDrawer from './CapabilityAssignDrawer';
import RoleAssignDrawer from './RoleAssignDrawer';
import PermissionsTree from './PermissionsTree';
import AssignCapabilitiesTree from './AssignCapabilitiesTree';



// ── Main Page ─────────────────────────────────────────────────────────────────
const UserPermissionsPage = ({ user, onBack }) => {
  const { data: permissions, isLoading, refetch } = useUserPermissions(user?.id);
  const { data: allCapabilities } = useCapabilities();
  const { data: allRoles } = useRoles();
  const { data: resourceTypes } = useResourceTypes();
  const [activeTab, setActiveTab] = useState('overview');
  const [capSearch, setCapSearch] = useState('');
  const [drawerCap, setDrawerCap] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [drawerRole, setDrawerRole] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const resourceTypeMap = useMemo(() => {
    const map = {};
    (resourceTypes || []).forEach(rt => { map[rt.name] = rt; });
    return map;
  }, [resourceTypes]);

  const roleAssignments = useMemo(() => (permissions || []).filter(p => p.roleId), [permissions]);
  const directCaps = useMemo(() => (permissions || []).filter(p => !p.roleId), [permissions]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleAssignCap = (cap) => setDrawerCap(cap);

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'assign-cap', label: '+ Assign Capability' },
    { key: 'assign-role', label: '+ Assign Role' },
  ];

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading permissions...</p>
      </div>
    );
  }

  return (
    <div className="user-permissions-page">
      {/* Header */}
      <div className="user-perm-header">
        <button className="auth-back-btn" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
          Back to Users
        </button>
        <div className="user-perm-identity">
          <div className="user-avatar-lg">
            {(user.name || user.email || '?').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h4>{user.name || user.email}</h4>
            <p className="text-muted mb-0">{user.email}</p>
          </div>
        </div>
      </div>

      {successMsg && <Alert variant="success" className="auth-success-alert">{successMsg}</Alert>}

      {/* Stats */}
      <div className="perm-stats-row">
        <div className="perm-stat-card">
          <span className="perm-stat-number">{roleAssignments.length}</span>
          <span className="perm-stat-label">Role Assignments</span>
        </div>
        <div className="perm-stat-card">
          <span className="perm-stat-number">{directCaps.length}</span>
          <span className="perm-stat-label">Direct Capabilities</span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="perm-tab-bar">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`perm-tab-btn ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="perm-section">
          {roleAssignments.length === 0 && directCaps.length === 0 ? (
            <div className="auth-empty-section">
              <FontAwesomeIcon icon={faShieldAlt} />
              <p>No permissions assigned yet</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button className="auth-btn-primary" onClick={() => setActiveTab('assign-role')}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />Assign Role
                </Button>
                <Button className="auth-btn-primary" onClick={() => setActiveTab('assign-cap')}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />Assign Capability
                </Button>
              </div>
            </div>
          ) : (
            <>
              {roleAssignments.length > 0 && (
                <div className="mb-4">
                  <div className="perm-overview-section-title">
                    <FontAwesomeIcon icon={faUserTag} className="me-2" />
                    Assigned Roles
                  </div>
                  <PermissionsTree assignments={roleAssignments} type="role" />
                </div>
              )}
              {directCaps.length > 0 && (
                <div>
                  <div className="perm-overview-section-title">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Direct Capabilities
                  </div>
                  <PermissionsTree assignments={directCaps} type="capability" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Assign Capability */}
      {activeTab === 'assign-cap' && (
        <div className="perm-section">
          <div className="perm-section-search">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search capabilities..."
              value={capSearch}
              onChange={e => setCapSearch(e.target.value)}
              className="perm-search-input"
            />
          </div>
          <AssignCapabilitiesTree
            capabilities={allCapabilities}
            onAssign={handleAssignCap}
            search={capSearch}
          />
        </div>
      )}

      {/* Assign Role */}
      {activeTab === 'assign-role' && (
        <div className="perm-section">
          {selectedRole ? (
            <RoleDetailPage
              role={selectedRole}
              userId={user?.id}
              onBack={() => setSelectedRole(null)}
              onAssigned={(msg) => {
                refetch();
                showSuccess(msg);
                setSelectedRole(null);
              }}
            />
          ) : (
            <div className="roles-assign-grid">
              {allRoles.map(role => (
                <div key={role.id} className="role-assign-card">
                  <div style={{ flex: 1 }}>
                    <strong>{role.name}</strong>
                  </div>
                  <Button
                    className="auth-btn-assign"
                    onClick={() => setSelectedRole(role)}
                  >
                    View & Assign
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Capability Assign Drawer */}
      <CapabilityAssignDrawer
        show={!!drawerCap}
        onHide={() => setDrawerCap(null)}
        capability={drawerCap}
        resourceTypeMap={resourceTypeMap}
        userId={user?.id}
        onAssigned={() => {
          refetch();
          showSuccess(`Capability "${drawerCap?.name}" assigned`);
          setDrawerCap(null);
        }}
      />

      {/* Role Assign Drawer */}
      <RoleAssignDrawer
        show={!!drawerRole}
        onHide={() => setDrawerRole(null)}
        role={drawerRole}
        userId={user?.id}
        onAssigned={() => {
          refetch();
          showSuccess(`Role "${drawerRole?.name}" assigned`);
          setDrawerRole(null);
        }}
      />
    </div>
  );
};

export default UserPermissionsPage;